import { ParcelObjectCategoryPicker } from "@/components/RadioGroup/ParcelObjectCategoryPicker.tsx";
import { VehicleNoteFI } from "@/components/ReservationOptions/VehicleNote/enums/VehicleNoteFI.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { RootAttribute } from "@/stores/EngineStore/enums/RootAttribute.ts";
import { CartAccommodationItem } from "@/stores/EngineStore/interfaces/CartAccommodationItem.ts";
import { ChangeEvent, FocusEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

/**
 * Camp specific component --> feature determined through data-attribute
 * data-vehicle_note="true"
 */
export const VehicleNote = ({ cartItem }: { cartItem: CartAccommodationItem }) => {
  const { t } = useTranslation();
  const [vehicleSizeError, setVehicleSizeError] = useState<string | null>(null);
  const {
    rootAttributes,
    setCartItemParcelObjectDimensions,
    setCartItemParcelObjectAdditionalTentDimensions,
  } = useEngineStore(
    useShallow((state) => ({
      rootAttributes: state.rootAttributes,
      setCartItemParcelObjectDimensions: state.setCartItemParcelObjectDimensions,
      setCartItemParcelObjectAdditionalTentDimensions: state.setCartItemParcelObjectAdditionalTentDimensions,
    })),
  );

  const areDimensionsMandatory = rootAttributes[RootAttribute.ParcelVehiclePickerDimensionsMandatory];
  const isParcel = cartItem?.ratePlan?.rateGroup?.isParcel;

  if (rootAttributes[RootAttribute.HideParcelVehiclePicker] || !isParcel) return null;

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>, type: VehicleNoteFI) => {
    const value = e.target.value;
    if (type === VehicleNoteFI.VehicleSize) {
      setCartItemParcelObjectDimensions(value, cartItem?.id);
    } else if (type === VehicleNoteFI.AdditionalTentSize) {
      setCartItemParcelObjectAdditionalTentDimensions(value, cartItem?.id);
    }

    // Clear error message when user starts typing
    if (value) {
      if (type === VehicleNoteFI.VehicleSize) {
        setVehicleSizeError(null);
      }
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>, type: VehicleNoteFI) => {
    const value = e.target.value;
    if (type === VehicleNoteFI.VehicleSize && !value) {
      setVehicleSizeError(t("form.requiredField"));
    }
  };

  return (
    <li className={`option c-reservation-option c-reservation-option--mandatory`}>
      <div className="c-reservation-option__left">
        <p className="c-reservation-option__title u-p1">{t("campSpecific.parcelVehicle")}</p>
      </div>
      <div className="vehicle-note-wrap">
        <ParcelObjectCategoryPicker cartItem={cartItem} />
        <div className="vehicle-note">
          <div className="vehicle-dimensions-wrap">
            <div className="vehicle-note-input-wrap">
              <fieldset className="formField c-be-booking-form__field">
                <label htmlFor={VehicleNoteFI.VehicleSize}>
                  {t("campSpecific.size")}
                  {areDimensionsMandatory && " *"}
                </label>
                <input
                  name={VehicleNoteFI.VehicleSize}
                  type="text"
                  placeholder={t("campSpecific.vehicleNotePlaceholder")}
                  value={cartItem.parcelObjectDimensions}
                  onChange={(e) => handleInputChange(e, VehicleNoteFI.VehicleSize)}
                  required={areDimensionsMandatory}
                  onBlur={(e) => handleBlur(e, VehicleNoteFI.VehicleSize)}
                />
                {vehicleSizeError && <span className="vehicle-err">{vehicleSizeError}</span>}
              </fieldset>
            </div>
            <div className="vehicle-note-input-wrap">
              <fieldset className="formField c-be-booking-form__field">
                <label htmlFor={VehicleNoteFI.AdditionalTentSize}>
                  {t("campSpecific.additionalTentSize")}
                </label>
                <input
                  name={VehicleNoteFI.AdditionalTentSize}
                  type="text"
                  placeholder={t("campSpecific.vehicleNotePlaceholder")}
                  value={cartItem.parcelObjectAdditionalTentDimensions}
                  onChange={(e) => handleInputChange(e, VehicleNoteFI.AdditionalTentSize)}
                  onBlur={(e) => handleBlur(e, VehicleNoteFI.AdditionalTentSize)}
                />
              </fieldset>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};
